import * as React from 'react';

import { HStack, RadioCard, Stack, Text, chakra, useRadioCardStyles } from '@chakra-ui/react';
import { Error as ErrorIcon } from '@m/icons';

export interface RadioCardItemProps extends RadioCard.ItemProps {
  addon?: React.ReactNode;
  badge?: React.ReactNode;
  description?: React.ReactNode;
  errorText?: React.ReactNode;
  icon?: React.ReactElement;
  indicator?: React.ReactNode | null;
  indicatorPlacement?: 'start' | 'end' | 'inside';
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  label?: React.ReactNode;
  ref?: React.Ref<HTMLInputElement>;
}

export const RadioCardItem = ({
  inputProps,
  label,
  badge,
  description,
  errorText,
  addon,
  icon,
  indicator = <RadioCard.ItemIndicator />,
  indicatorPlacement = 'start',
  ref,
  ...rest
}: RadioCardItemProps) => {
  const styles = useRadioCardStyles();
  const hasContent = label || description;
  const ContentWrapper = indicator ? RadioCard.ItemContent : React.Fragment;
  const isInvalid = !!errorText;

  return (
    <Stack gap={2} width="100%">
      <RadioCard.Item invalid={isInvalid} {...rest}>
        <RadioCard.ItemHiddenInput ref={ref} {...inputProps} />
        <RadioCard.ItemControl>
          {indicatorPlacement === 'start' && indicator}
          {hasContent && (
            <ContentWrapper>
              {label && (
                <HStack gap={2}>
                  <RadioCard.ItemText>{label}</RadioCard.ItemText>
                  {badge}
                </HStack>
              )}
              {description && <RadioCard.ItemDescription>{description}</RadioCard.ItemDescription>}
              {indicatorPlacement === 'inside' && indicator}
            </ContentWrapper>
          )}
          {indicatorPlacement === 'end' && indicator}
          {icon && <chakra.div css={[styles.itemIcon['@layer recipes']]}>{icon}</chakra.div>}
        </RadioCard.ItemControl>
        {addon && <RadioCard.ItemAddon>{addon}</RadioCard.ItemAddon>}
      </RadioCard.Item>
      {errorText && (
        <HStack gap={1} color="text.error">
          <ErrorIcon />
          <Text variant="ExtraSmallBodyRegular">{errorText}</Text>
        </HStack>
      )}
    </Stack>
  );
};

export const RadioCardRoot = RadioCard.Root;
export const RadioCardLabel = RadioCard.Label;
export const RadioCardItemIndicator = RadioCard.ItemIndicator;
